import { Link } from 'react-router-dom';
import { CalendarPlus } from 'lucide-react';
import { Button, Card, CardContent, Badge } from '@leoclinic/ui';
import { useMyAppointments, useClientPortalMutations } from '@leoclinic/hooks';
import { formatDateTime } from '@leoclinic/utils';
import { BRAND_COLORS } from '@leoclinic/types';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  confirmed: 'Confirmada',
  in_progress: 'Em curso',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  no_show: 'Não compareceu',
};

function canCancel(status: string, startAt: string): boolean {
  return ['pending', 'confirmed'].includes(status) && new Date(startAt) > new Date();
}

export default function AppointmentsPage() {
  const { data: appointments, isLoading, error } = useMyAppointments();
  const { cancelAppointment, isCancelling } = useClientPortalMutations();

  const handleCancel = async (id: string) => {
    if (!window.confirm('Tem a certeza que deseja cancelar esta marcação?')) return;
    await cancelAppointment(id);
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">A carregar marcações...</p>;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-destructive">
          Não foi possível carregar as marcações. Tente sair e entrar novamente.
        </CardContent>
      </Card>
    );
  }

  const upcoming = (appointments ?? []).filter(
    (a) => a.status !== 'cancelled' && a.status !== 'completed' && new Date(a.start_at) >= new Date(),
  );
  const past = (appointments ?? []).filter(
    (a) => a.status === 'completed' || a.status === 'cancelled' || new Date(a.start_at) < new Date(),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold" style={{ color: BRAND_COLORS.greenDark }}>
          Minhas marcações
        </h1>
        <Button asChild size="sm">
          <Link to="/">
            <CalendarPlus className="h-4 w-4 mr-1.5" />
            Nova
          </Link>
        </Button>
      </div>

      {!(appointments ?? []).length ? (
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-sm text-muted-foreground">Ainda não tem marcações associadas a esta conta.</p>
            <Button asChild>
              <Link to="/">Marcar sessão</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Próximas</h2>
              {upcoming.map((apt) => (
                <AppointmentCard
                  key={apt.id}
                  apt={apt}
                  onCancel={canCancel(apt.status, apt.start_at) ? () => handleCancel(apt.id) : undefined}
                  isCancelling={isCancelling}
                />
              ))}
            </section>
          )}
          {past.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Histórico</h2>
              {past.map((apt) => (
                <AppointmentCard key={apt.id} apt={apt} />
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function AppointmentCard({
  apt,
  onCancel,
  isCancelling,
}: {
  apt: {
    id: string;
    start_at: string;
    status: string;
    treatment_name: string;
    therapist_name: string;
    notes: string | null;
  };
  onCancel?: () => void;
  isCancelling?: boolean;
}) {
  return (
    <Card className="border-[#ECECEC]">
      <CardContent className="pt-4 pb-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold" style={{ color: BRAND_COLORS.greenDark }}>
              {apt.treatment_name}
            </p>
            <p className="text-sm text-muted-foreground">{apt.therapist_name}</p>
          </div>
          <Badge variant={apt.status === 'cancelled' ? 'secondary' : 'default'}>
            {STATUS_LABELS[apt.status] ?? apt.status}
          </Badge>
        </div>
        <p className="text-sm font-medium">{formatDateTime(apt.start_at)}</p>
        {apt.notes && apt.notes !== 'Marcação online' && (
          <p className="text-xs text-muted-foreground">{apt.notes}</p>
        )}
        {onCancel && (
          <Button
            variant="outline"
            size="sm"
            className="mt-1"
            disabled={isCancelling}
            onClick={onCancel}
          >
            Cancelar marcação
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
